import { db } from "./index.js";
import { addRouteLocation } from "./routeLocations.js";
import { distanceMetersBetween } from "../utils.js";

// GPS trail recording gates. The trail should show meaningful movement, not
// one point per sample: full-page navigations between samples used to re-fire
// an immediate fix on every connect(), producing ~1 routeLocation per sample
// (87% of consecutive moves < 2m on real routes). The last recorded point is
// persisted on the sanitationRoutes record so the gate survives page loads.
//
// Server counterpart: SanitationRoute::TrailSimplification decimates trails
// again at display time with MIN_DISTANCE_M = 5 (< TRAIL_MIN_DISTANCE_M, so
// points recorded here are never collapsed away) and the same 30m accuracy
// cap. Keep that relationship when tuning these.
export const TRAIL_MIN_DISTANCE_M = 10;
export const TRAIL_MIN_SPACING_MS = 15000;
export const TRAIL_MAX_INTERVAL_MS = 300000;
export const TRAIL_MAX_ACCURACY_M = 30;
// Faster than a jog between the last recorded point and the candidate is a
// GPS glitch or stale cached fix, not the monitor walking.
export const TRAIL_MAX_SPEED_M_S = 3;

// Cheap time-only pre-check for pages without a running position watch:
// callers should only REQUEST a GPS fix when this passes.
export function isTrailRequestDue(route, now = Date.now()) {
  const lastAt = lastRecordedAtMs(route);
  if (lastAt == null) return true;
  return now - lastAt >= TRAIL_MIN_SPACING_MS;
}

export function isTrailRecordDue(route, position, now = Date.now()) {
  if (!route || !position?.coords) return false;

  const accuracy = position.coords.accuracy;
  if (Number.isFinite(accuracy) && accuracy > TRAIL_MAX_ACCURACY_M) {
    return false;
  }

  const lastAt = lastRecordedAtMs(route);
  if (lastAt == null) return true;

  const elapsedMs = now - lastAt;
  const distanceM = distanceMetersBetween(
    { lat: route.last_location_lat, lon: route.last_location_lon },
    { lat: position.coords.latitude, lon: position.coords.longitude },
  );

  // Implied-speed cap: teleport-fast candidates are glitches. Elapsed time
  // keeps growing while candidates are rejected, so a genuine relocation is
  // eventually accepted.
  if (
    distanceM != null &&
    elapsedMs > 0 &&
    distanceM / (elapsedMs / 1000) > TRAIL_MAX_SPEED_M_S
  ) {
    return false;
  }

  if (elapsedMs >= TRAIL_MAX_INTERVAL_MS) return true;
  if (elapsedMs < TRAIL_MIN_SPACING_MS) return false;

  return distanceM != null && distanceM >= TRAIL_MIN_DISTANCE_M;
}

// Shared controller entry point: guards the route, builds GPS metadata only
// after the gate passes (watch callbacks fire ~1/s; most candidates are
// rejected), and never throws.
export async function recordTrailPosition(route, position, buildGpsMetadata) {
  if (!route) return false;

  try {
    if (!isTrailRecordDue(route, position)) return false;

    return await maybeRecordRouteLocation(route, position, {
      gpsMetadata: buildGpsMetadata(),
    });
  } catch (error) {
    console.error("Failed to save route location:", error);
    return false;
  }
}

let recordingInFlight = false;

export async function maybeRecordRouteLocation(
  route,
  position,
  { gpsMetadata = null, now = Date.now() } = {},
) {
  if (recordingInFlight) return false;
  if (!isTrailRecordDue(route, position, now)) return false;

  recordingInFlight = true;
  try {
    const lat = position.coords.latitude;
    const lon = position.coords.longitude;

    await addRouteLocation({
      sanitationRouteId: route.id,
      lat,
      lon,
      gpsMetadata,
    });

    const recordedAt = new Date(now).toISOString();
    // Extra non-indexed fields don't require a Dexie schema version bump.
    await db.sanitationRoutes.update(route.id, {
      last_location_recorded_at: recordedAt,
      last_location_lat: lat,
      last_location_lon: lon,
    });
    route.last_location_recorded_at = recordedAt;
    route.last_location_lat = lat;
    route.last_location_lon = lon;
    return true;
  } finally {
    recordingInFlight = false;
  }
}

function lastRecordedAtMs(route) {
  if (!route?.last_location_recorded_at) return null;
  const parsed = Date.parse(route.last_location_recorded_at);
  return Number.isFinite(parsed) ? parsed : null;
}
